import { useContext, useState } from 'react';
import { trackMixpanelEvent } from '@belong/common';
import {
  MaintenancePaymentMethod,
  MaintenancePaymentPlan,
  PaymentMethod,
} from 'models/enums';
import { ApprovalContext } from 'pages/PostInspectionFlow/steps/Improvements/Approval/ApprovalContext';
import { PaymentStepperBackButton } from 'post-inspection/components/payment-stepper-back-button/payment-stepper-back-button';
import { PaygConfirmStep } from './confirm-step/payg-confirm-step';
import { PaygDeclinedStep } from './declined-step/payg-declined-step';
import { PaygMethodStep } from './method-step/payg-method-step';
import { PaygProcessingStep } from './processing-step/payg-processing-step';
import { usePaygPaymentMethod } from './use-payg-payment-method';

const FAILED_TRANSFER_STATUSES = ['Failed', 'Canceled'];
const UPFRONT_PAYMENT_POLL_ATTEMPTS = 5;
const UPFRONT_PAYMENT_POLL_INTERVAL_MS = 2000;
const UPFRONT_PAYMENT_MISSING_ERROR = 'payg-upfront-payment-missing';

export function PaygFlow() {
  const {
    flow,
    homeownerPayment,
    isSubmitting,
    onApprovalStepSubmit,
    onNext,
    onRefreshStepData,
    setIsSubmitting,
    upfrontPayment,
  } = useContext(ApprovalContext);
  const paymentMethod = usePaygPaymentMethod();

  const [isPickingMethod, setIsPickingMethod] = useState(!paymentMethod);
  const [hasPickedMethod, setHasPickedMethod] = useState(false);
  const [isPaymentUnconfirmed, setIsPaymentUnconfirmed] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting || isPaymentUnconfirmed || !paymentMethod) return;

    setIsSubmitting(true);

    try {
      const isAch = paymentMethod.paymentMethod === PaymentMethod.Ach;
      const isRetry = Boolean(upfrontPayment);
      const stepData = await onApprovalStepSubmit({
        groupPayment: {
          ...homeownerPayment,
          paymentPlan: MaintenancePaymentPlan.OneTime,
          installmentCount: 1,
          deferPaymentUntilLease: false,
          paymentMethod: isAch
            ? MaintenancePaymentMethod.Check
            : MaintenancePaymentMethod.CreditCard,
        },
        paymentAccountId: paymentMethod.accountId,
      });

      if (stepData?.upfrontPayment) return;

      let status: string | undefined;
      let attempts = 0;
      let recovered = false;

      // The charge can land even when the request fails, so wait for the backend to report it.
      for (
        let attempt = 0;
        attempt < UPFRONT_PAYMENT_POLL_ATTEMPTS;
        attempt++
      ) {
        if (attempt > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, UPFRONT_PAYMENT_POLL_INTERVAL_MS),
          );
        }

        const refreshed = await onRefreshStepData();
        status = refreshed?.upfrontPayment?.transferStatus;
        attempts = attempt + 1;

        recovered = Boolean(
          status && !(isRetry && FAILED_TRANSFER_STATUSES.includes(status)),
        );
        if (recovered) break;
      }

      if (!status) setIsPaymentUnconfirmed(true);

      trackMixpanelEvent('flow-error', {
        flowType: flow?.flowType,
        errorCode: UPFRONT_PAYMENT_MISSING_ERROR,
        paymentAttemptId: crypto.randomUUID(),
        flowId: flow?.uniqueId,
        paymentMethod: paymentMethod.paymentMethod,
        requestFailed: !stepData,
        isRetry,
        attempts,
        finalStatus: status ?? null,
        recovered,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMethodPicked = () => {
    setIsPickingMethod(false);
    setHasPickedMethod(true);
  };

  if (upfrontPayment && paymentMethod && !isPickingMethod) {
    if (FAILED_TRANSFER_STATUSES.includes(upfrontPayment.transferStatus)) {
      return (
        <PaygDeclinedStep
          canRetry={upfrontPayment.canRetry}
          isPaymentUnconfirmed={isPaymentUnconfirmed}
          onRetry={handleSubmit}
          paymentMethod={paymentMethod}
        />
      );
    }

    return (
      <PaygProcessingStep
        isReportConfirmed={upfrontPayment.isReportConfirmed}
        isUpfrontCompleted={upfrontPayment.transferStatus === 'Completed'}
        onDone={() => onNext({ refreshFlow: true })}
        paymentMethod={paymentMethod}
      />
    );
  }

  if (isPickingMethod || !paymentMethod) {
    return <PaygMethodStep onDone={handleMethodPicked} />;
  }

  return (
    <>
      {hasPickedMethod && (
        <PaymentStepperBackButton onClick={() => setIsPickingMethod(true)} />
      )}
      <PaygConfirmStep
        isPaymentUnconfirmed={isPaymentUnconfirmed}
        onConfirm={handleSubmit}
      />
    </>
  );
}
